import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Select as MuiSelect,
  FormHelperText,
} from "@mui/material";

import AppForm from "components/Shared/AppForm";
import ReactSelect from "components/Shared/ReactSelect";

import { useListAccountEntityUserQuery } from "store/services/userApi";

import { useAuth } from "hooks/useAuth";

const TeamForm = ({
  teamDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const [selectedTeamMembers, setSelectedTeamMembers] = React.useState([]);

  const navigate = useNavigate();

  const { viewEntity } = useAuth();

  const teamTypes = ["Department"];

  // RTK Query
  const { data: users, isLoading: loadingUsers } =
    useListAccountEntityUserQuery({
      entityId: viewEntity?.entity?._id,
      status: "Active",
    });

  const mappedUserList = React.useMemo(() => {
    return (
      users?.map((user) => ({
        label: user?.name,
        value: user?._id,
      })) ?? []
    );
  }, [users]);

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      name: teamDetail?.name ?? "",
      type: teamDetail?.type ?? "",
      members: teamDetail?.members
        ? teamDetail?.members?.map((m) => ({ label: m.name, value: m._id }))
        : [],
      manager: teamDetail?.manager ?? "",
      description: teamDetail?.description ?? "",
    },
  });

  // methods
  const handleOnTeamSubmit = (data) => {
    onSubmit({
      ...data,
      members: data?.members?.map((m) => m.value),
      manager: data?.manager?.value,
    });
  };

  React.useEffect(() => {
    if (!teamDetail) return;

    const teamMembers = teamDetail?.members
      ? teamDetail?.members?.map((m) => ({ label: m.name, value: m._id }))
      : [];
    const teamManager = teamMembers?.find(
      (m) => m.value === teamDetail?.manager?._id
    );

    setSelectedTeamMembers(teamMembers);

    reset({
      ...teamDetail,
      members: teamMembers,
      manager: teamManager,
    });
  }, [teamDetail, reset]);

  return (
    <AppForm loading={isBusy || loadingUsers}>
      <AppForm.Content>
        <Grid container spacing={6}>
          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide a Name." }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Team Name"
                  type="text"
                  size="small"
                  {...field}
                />
              )}
            />
            {!!errors.name && (
              <FormHelperText error>{errors.name.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select a Type." }}
              render={({ field, value }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-type">Type</InputLabel>

                  <MuiSelect
                    fullWidth
                    labelId="select-type"
                    label="Course Level"
                    placeholder="Select Type"
                    size="small"
                    id="role"
                    value={value ? value : ""}
                    {...field}
                  >
                    {teamTypes?.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              )}
            />
            {!!errors.type && (
              <FormHelperText error>{errors.type.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={6}>
            <Controller
              name="members"
              control={control}
              rules={{ required: "Please select Team members." }}
              render={({ field: { onChange, value } }) => (
                <Box className="-my-2">
                  <FormHelperText>Team Members</FormHelperText>

                  <ReactSelect
                    isMulti
                    isClearable
                    value={value}
                    options={mappedUserList}
                    onChange={(e) => {
                      onChange(e);
                      setValue("manager", "");
                      setSelectedTeamMembers(e);
                    }}
                  />
                  {!!errors.members && (
                    <FormHelperText error>
                      {errors.members.message}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="manager"
              control={control}
              rules={{ required: "Select a Team Manager." }}
              render={({ field }) => (
                <Box className="-my-2">
                  <FormHelperText>Team Manager</FormHelperText>

                  <ReactSelect
                    isClearable
                    options={selectedTeamMembers}
                    {...field}
                  />

                  {!!errors.manager && (
                    <FormHelperText error>
                      {errors.manager.message}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
          </Grid>

          <Grid item md={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Please provide a Description." }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Description"
                  type="text"
                  size="small"
                  multiline
                  rows={3}
                  {...field}
                />
              )}
            />
            {!!errors.description && (
              <FormHelperText error>
                {errors.description.message}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>

        <Button variant="contained" onClick={handleSubmit(handleOnTeamSubmit)}>
          {teamDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default TeamForm;
